import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { fieldClass, ghostBtn, iconBtn } from '../components/dataModelsUi';
import { emptyGroup, emptyPredicate } from './conditionNodes';
import { ALL_VARIABLES } from './variables';
import type { ConditionGroup, ConditionNode, ConditionOperator, PolicyAsset, ValueKind } from './types';
import { OPERATORS } from './types';
import { columnGroups, usePolicyAssetColumns, type PolicyColumnGroup } from './usePolicyAssetColumns';
import { usePolicyDirectory } from './usePolicyDirectory';

export { emptyGroup, emptyPredicate } from './conditionNodes';

function replaceNode(root: ConditionNode, id: string, next: ConditionNode | null, idPrefix = ''): ConditionNode {
  if (root.id === id) return next ?? emptyGroup(idPrefix);
  if (root.kind !== 'group') return root;
  return {
    ...root,
    children: root.children
      .map((child) => (child.id === id ? next : replaceNode(child, id, next, idPrefix)))
      .filter((child): child is ConditionNode => Boolean(child)),
  };
}

function withCurrentColumn(groups: PolicyColumnGroup[], name: string): PolicyColumnGroup[] {
  if (!name || groups.some((group) => group.columns.some((column) => column.name === name || column.qualified === name))) return groups;
  return [
    ...groups,
    {
      id: 'selected',
      label: 'Selected',
      columns: [{ name, label: name, qualified: name, assetId: '', group: 'Selected' }],
    },
  ];
}

export function TreeChildren({
  isLight,
  children,
}: {
  isLight: boolean;
  children: React.ReactNode;
}) {
  const items = React.Children.toArray(children).filter(Boolean);
  const line = isLight ? 'bg-[#E6E8EB]' : 'bg-white/10';
  return (
    <div className="ml-3">
      {items.map((child, index) => {
        const last = index === items.length - 1;
        const key = React.isValidElement(child) && child.key != null ? String(child.key) : String(index);
        return (
          <div key={key} className="relative pl-5">
            <span className={`pointer-events-none absolute left-0 top-0 w-px ${last ? 'h-4' : 'bottom-0'} ${line}`} aria-hidden />
            <span className={`pointer-events-none absolute left-0 top-4 h-px w-5 ${line}`} aria-hidden />
            {child}
          </div>
        );
      })}
    </div>
  );
}

export function CombinatorToggle({
  value,
  onChange,
  isLight,
  disabled,
}: {
  value: 'AND' | 'OR';
  onChange: (value: 'AND' | 'OR') => void;
  isLight: boolean;
  disabled?: boolean;
}) {
  return (
    <div className={`flex border ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
      {(['AND', 'OR'] as const).map((item) => (
        <button
          key={item}
          type="button"
          disabled={disabled}
          aria-pressed={value === item}
          onClick={() => onChange(item)}
          className={`h-7 px-2 text-[11px] ${
            value === item
              ? isLight ? 'bg-[#F3F4F6] font-medium text-[#111827]' : 'bg-white/10 font-medium text-white'
              : isLight ? 'text-[#6B7280]' : 'text-slate-400'
          }`}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

export function GroupActions({
  group,
  onChange,
  isLight,
  idPrefix,
  disabled,
  onRemove,
  label,
}: {
  group: ConditionGroup;
  onChange: (node: ConditionGroup) => void;
  isLight: boolean;
  idPrefix: string;
  disabled?: boolean;
  onRemove?: () => void;
  label?: string;
}) {
  const actionBtn = `${ghostBtn(isLight)} !h-7 !rounded-none !px-2 !text-[11px]`;
  return (
    <>
      {label ? <span className={`text-[13px] font-medium ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>{label}</span> : null}
      <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">
        <CombinatorToggle
          value={group.combinator}
          onChange={(combinator) => onChange({ ...group, combinator })}
          isLight={isLight}
          disabled={disabled}
        />
        <button
          type="button"
          disabled={disabled}
          className={actionBtn}
          onClick={() => onChange({ ...group, children: [...group.children, emptyPredicate(idPrefix)] })}
        >
          <Plus size={11} />
          Condition
        </button>
        <button
          type="button"
          disabled={disabled}
          className={actionBtn}
          onClick={() => onChange({ ...group, children: [...group.children, emptyGroup(idPrefix)] })}
        >
          <Plus size={11} />
          Group
        </button>
        {onRemove ? (
          <button type="button" disabled={disabled} className={iconBtn(isLight)} aria-label="Remove group" onClick={onRemove}>
            <Trash2 size={13} />
          </button>
        ) : null}
      </div>
    </>
  );
}

export default function ConditionBuilder({
  node,
  onChange,
  assets,
  isLight,
  idPrefix = '',
  hideRootToolbar = false,
}: {
  node: ConditionNode;
  onChange: (node: ConditionNode) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  idPrefix?: string;
  hideRootToolbar?: boolean;
}) {
  const { assets: withColumns, loading, error } = usePolicyAssetColumns(assets);
  const groups = columnGroups(withColumns);
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const hint = !assets.length
    ? 'Select a table in Applies to to list its columns.'
    : loading
      ? 'Loading columns from the selected table…'
      : error
        ? error
        : !groups.length
          ? 'No columns found on the selected table. Caller variables are not table fields.'
          : '';

  return (
    <div>
      <NodeEditor
        node={node}
        onChange={onChange}
        onReplace={(id, next) => onChange(replaceNode(node, id, next, idPrefix))}
        groups={groups}
        loading={loading}
        isLight={isLight}
        idPrefix={idPrefix}
        hideToolbar={hideRootToolbar}
      />
      {hint ? <p className={`mt-2 text-xs ${error ? (isLight ? 'text-rose-600' : 'text-rose-300') : muted}`}>{hint}</p> : null}
    </div>
  );
}

function NodeEditor({
  node,
  onChange,
  onReplace,
  groups,
  loading,
  isLight,
  idPrefix,
  nested = false,
  hideToolbar = false,
  onRemove,
}: {
  node: ConditionNode;
  onChange: (node: ConditionNode) => void;
  onReplace: (id: string, next: ConditionNode | null) => void;
  groups: PolicyColumnGroup[];
  loading: boolean;
  isLight: boolean;
  idPrefix: string;
  nested?: boolean;
  hideToolbar?: boolean;
  onRemove?: () => void;
}) {
  const directory = usePolicyDirectory();
  const [open, setOpen] = useState(true);

  if (node.kind === 'group') {
    const childTree = node.children.length ? (
      <TreeChildren isLight={isLight}>
        {node.children.map((child) => (
          <NodeEditor
            key={child.id}
            node={child}
            onChange={(next) => onReplace(child.id, next)}
            onReplace={onReplace}
            groups={groups}
            loading={loading}
            isLight={isLight}
            idPrefix={idPrefix}
            nested={child.kind === 'group'}
            onRemove={() => onReplace(child.id, null)}
          />
        ))}
      </TreeChildren>
    ) : null;

    if (hideToolbar) return childTree;

    const actions = (
      <GroupActions
        group={node}
        onChange={onChange}
        isLight={isLight}
        idPrefix={idPrefix}
        onRemove={onRemove}
        label={nested ? 'Group' : undefined}
      />
    );

    if (nested) {
      return (
        <div>
          <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? 'Collapse group' : 'Expand group'}
              className={iconBtn(isLight)}
              onClick={() => setOpen((current) => !current)}
            >
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
            {actions}
          </div>
          {open ? childTree : null}
        </div>
      );
    }

    return (
      <div>
        <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">{actions}</div>
        {childTree}
      </div>
    );
  }

  const unary = node.operator === 'is_null' || node.operator === 'is_not_null';
  const fieldGroups = node.left.kind === 'column' ? withCurrentColumn(groups, node.left.name) : groups;
  const valueGroups = node.right?.kind === 'column' ? withCurrentColumn(groups, node.right.value) : groups;
  const fieldValue = `${node.left.kind}:${node.left.name}`;
  const compactField = `${fieldClass(isLight)} !h-8 !rounded-none !py-0 !text-[13px]`;

  return (
    <div className={`grid min-h-8 items-center gap-2 py-0.5 ${unary ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]' : 'md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]'}`}>
      <select
        aria-label="Field"
        className={compactField}
        value={fieldValue}
        onChange={(event) => {
          const [kind, ...rest] = event.target.value.split(':');
          onChange({ ...node, left: { kind: kind as 'column' | 'variable', name: rest.join(':') } });
        }}
      >
        <option value="column:">{loading ? 'Loading columns…' : 'Select field'}</option>
        {fieldGroups.map((group) => (
          <optgroup key={group.id} label={group.label}>
            {group.columns.map((column) => (
              <option key={`${column.assetId}:${column.name}`} value={`column:${column.name}`}>{column.label}</option>
            ))}
          </optgroup>
        ))}
        <optgroup label="Caller variables">
          {ALL_VARIABLES.map((item) => (
            <option key={item.path} value={`variable:${item.path}`}>{`\${${item.path}}`}</option>
          ))}
        </optgroup>
      </select>
      <select aria-label="Operator" className={compactField} value={node.operator} onChange={(event) => onChange({ ...node, operator: event.target.value as ConditionOperator })}>
        {OPERATORS.map((item) => (
          <option key={item.id} value={item.id}>{item.label}</option>
        ))}
      </select>
      {!unary ? (
        <>
          <select
            aria-label="Compare to"
            className={compactField}
            value={node.right?.kind ?? 'constant'}
            onChange={(event) => onChange({ ...node, right: { kind: event.target.value as ValueKind, value: node.right?.value ?? '' } })}
          >
            <option value="constant">Constant</option>
            <option value="user_var">User variable</option>
            <option value="request_var">Request variable</option>
            <option value="resource_var">Resource variable</option>
            <option value="column">Column</option>
          </select>
          {node.right?.kind === 'user_var' || node.right?.kind === 'request_var' || node.right?.kind === 'resource_var' ? (
            <select aria-label="Value" className={compactField} value={node.right.value} onChange={(event) => onChange({ ...node, right: { kind: node.right?.kind ?? 'user_var', value: event.target.value } })}>
              <option value="">Select variable</option>
              {ALL_VARIABLES.filter((item) => item.path.startsWith(node.right?.kind === 'request_var' ? 'request' : node.right?.kind === 'resource_var' ? 'resource' : 'user')).map((item) => (
                <option key={item.path} value={item.path}>{item.path}</option>
              ))}
            </select>
          ) : node.right?.kind === 'column' ? (
            <select aria-label="Value" className={compactField} value={node.right.value} onChange={(event) => onChange({ ...node, right: { kind: 'column', value: event.target.value } })}>
              <option value="">{loading ? 'Loading columns…' : 'Select column'}</option>
              {valueGroups.map((group) => (
                <optgroup key={group.id} label={group.label}>
                  {group.columns.map((column) => (
                    <option key={`${column.assetId}:${column.name}`} value={column.name}>{column.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          ) : node.left.kind === 'variable' && node.left.name === 'user.id' ? (
            <select
              aria-label="Person"
              className={compactField}
              value={node.right?.value ?? ''}
              onChange={(event) => onChange({ ...node, right: { kind: 'constant', value: event.target.value } })}
            >
              <option value="">{directory.loading ? 'Loading people…' : directory.error || 'Select person'}</option>
              {(node.right?.value && !directory.users.some((user) => user.id === node.right?.value)
                ? [{ id: node.right.value, label: node.right.value }, ...directory.users]
                : directory.users
              ).map((user) => (
                <option key={user.id} value={user.id}>{user.label}</option>
              ))}
            </select>
          ) : (
            <input aria-label="Value" className={compactField} value={node.right?.value ?? ''} onChange={(event) => onChange({ ...node, right: { kind: 'constant', value: event.target.value } })} placeholder="5000 or US" />
          )}
        </>
      ) : null}
      {onRemove ? (
        <button type="button" className={iconBtn(isLight)} aria-label="Remove condition" onClick={onRemove}>
          <Trash2 size={13} />
        </button>
      ) : null}
    </div>
  );
}
